import { Component, inject, effect } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Company } from '../../model/user';
import { CompanyService } from 'src/app/admin/services/company.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { Tooltip } from 'primeng/tooltip';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import {
  LucideAngularModule,
  Building2,
  MapPin,
  TriangleAlert,
} from 'lucide-angular';
import { isValidVatFormat } from 'src/app/validators/vat.validators';
import { DropdownOption } from 'src/app/auth/models/registration.models';
import { SelectModule } from 'primeng/select';
import { LogoUploadComponent } from 'src/app/shared/components/logo-upload/logo-upload.component';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-company-informations',
  imports: [
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    TranslocoModule,
    Tooltip,
    LucideAngularModule,
    SelectModule,
    LogoUploadComponent,
  ],
  templateUrl: './company-informations.component.html',
  styleUrl: './company-informations.component.scss',
})
export class CompanyInformationsComponent {
  /** Build-time switch, mirrors FEATURE_*_ENABLED on the API. */
  protected readonly features = environment.features;
  private fb = inject(FormBuilder);
  private companyService = inject(CompanyService);
  private translocoService = inject(TranslocoService);
  currentUserService = inject(CurrentUserService);
  private notificationService = inject(NotificationService);

  readonly buildingIcon = Building2;
  readonly mapPinIcon = MapPin;
  readonly alertIcon = TriangleAlert;

  company?: Company;
  isLoading = false;
  logoUploading = false;

  /** Check if VAT format is invalid (for warning display, not blocking) */
  get vatFormatWarning(): {
    key: string;
    params?: Record<string, string>;
  } | null {
    const vat = this.companyForm.get('vatNumber')?.value;
    if (!vat || vat.length < 4) return null;
    if (isValidVatFormat(vat)) return null;
    const countryCode = vat.toUpperCase().substring(0, 2);
    if (!/^[A-Z]{2}$/.test(countryCode)) {
      return {
        key: 'accountMenu.organizationsManagement.vatFormatInvalidNoCountry',
      };
    }
    return {
      key: 'accountMenu.organizationsManagement.vatFormatInvalidForCountry',
      params: { countryCode },
    };
  }

  companyForm = this.fb.group({
    name: ['', Validators.required],
    vatNumber: [''],
    street: ['', Validators.required],
    streetNumber: ['', Validators.required],
    city: ['', Validators.required],
    zipCode: ['', Validators.required],
    country: ['', Validators.required],
    // Pour l'instant on ne veut pas que l'utilisateur puisse changer sa companySize donc j'ai commenté le code à ce sujet
    // size: ['', Validators.required],
  });

  sizeOptions: DropdownOption[] = [];

  constructor() {
    effect(() => {
      this.company = this.currentUserService.currentUser()?.company;
      this.company && this.populateForm(this.company);
    });
  }

  // ngOnInit() {
  //   const rawOptions = Object.values(CompanySize);
  //   for (const option of rawOptions) {
  //     const label = this.translocoService.translate(`companySize.${option}`);
  //     const value = option;
  //     this.sizeOptions.push({ label, value });
  //   }
  // }

  populateForm(company: Company) {
    this.companyForm.patchValue({
      name: company?.name ?? '',
      vatNumber: company?.vatNumber ?? '',
      street: company?.address?.street ?? '',
      streetNumber: company?.address?.streetNumber ?? '',
      city: company?.address?.city ?? '',
      zipCode: company?.address?.zipCode ?? '',
      country: company?.address?.country ?? '',
      // size: company?.size,
    });
  }

  onSubmit() {
    if (this.companyForm.valid) {
      this.isLoading = true;
      const formValues = this.companyForm.value;
      if (!this.company?.id) {
        this.isLoading = false;
        return;
      }
      console.log('formValue', formValues);
      const companyDto = {
        ...this.company,
        name: formValues.name ?? '',
        vatNumber: formValues.vatNumber ?? '',
        // size: formValues.size as CompanySize,
        address: {
          street: formValues.street ?? '',
          streetNumber: formValues.streetNumber ?? '',
          city: formValues.city ?? '',
          zipCode: formValues.zipCode ?? '',
          country: formValues.country ?? '',
        },
      };
      this.companyService.updateCompany(this.company.id, companyDto).subscribe({
        next: () => {
          this.showSuccessMessage();
          this.currentUserService.refreshCurrentUser();
          this.isLoading = false;
        },
        error: () => {
          this.showWarningMessage();
          this.isLoading = false;
        },
      });
    } else {
      this.notificationService.error('Form is invalid');
    }
  }

  async copyPublicId() {
    const publicId = this.company?.publicId;
    if (!publicId) return;
    try {
      await navigator.clipboard.writeText(publicId);
      this.notificationService.success(
        this.translocoService.translate(
          'accountMenu.billingInformation.publicIdCopied',
        ),
      );
    } catch {
      // Clipboard API can be unavailable (permissions, non-secure context) —
      // the ID stays visible and selectable on screen either way.
    }
  }

  onLogoCropped(logo: Blob) {
    if (!this.company?.id) {
      return;
    }
    this.logoUploading = true;
    this.companyService.uploadLogo(this.company.id, logo).subscribe({
      next: () => {
        this.currentUserService.refreshCurrentUser();
        this.logoUploading = false;
        this.notificationService.success(
          this.translocoService.translate('logoUpload.success'),
        );
      },
      error: () => {
        this.logoUploading = false;
        this.notificationService.error(
          this.translocoService.translate('logoUpload.error'),
        );
      },
    });
  }

  showSuccessMessage() {
    this.notificationService.success(
      this.translocoService.translate(
        'accountMenu.billingInformation.updateBillingSuccess',
      ),
    );
  }

  showWarningMessage() {
    this.notificationService.warn(
      this.translocoService.translate(
        'accountMenu.billingInformation.updateBillingError',
      ),
    );
  }
}
